import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useChatStore } from "@/stores/chat";
import { useProjectStore } from "@/stores/projects";
import { ChatMessage } from "@/components/chat/ChatMessage";
import { ChatInput } from "@/components/chat/ChatInput";

export function ChatView() {
  const { id } = useParams();
  const { messages, streaming, streamingContent, error, loadHistory, sendMessage } =
    useChatStore();
  const { projects, activeProject, setActiveProject } = useProjectStore();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [selectedProject, setSelectedProject] = useState<string | null>(id || null);

  // Set active project from URL
  useEffect(() => {
    if (id) {
      const project = projects.find((p) => p.id === id);
      if (project) {
        setActiveProject(project);
        setSelectedProject(project.id);
      }
    }
  }, [id, projects, setActiveProject]);

  // Load history when project changes
  useEffect(() => {
    if (selectedProject) {
      loadHistory(selectedProject);
    }
  }, [selectedProject, loadHistory]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamingContent]);

  const handleSend = async (content: string) => {
    if (!selectedProject) return;
    await sendMessage(selectedProject, content);
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex h-14 items-center justify-between border-b border-zinc-800 px-4">
        <div className="flex items-center gap-3">
          <h1 className="text-sm font-medium text-zinc-200">
            {activeProject?.name || "Chat"}
          </h1>
          {activeProject && (
            <span className="rounded-md bg-zinc-800 px-2 py-0.5 text-xs text-zinc-400">
              {activeProject.sessionKey ? "● Connected" : "○ No session"}
            </span>
          )}
        </div>

        {/* Project selector */}
        <select
          value={selectedProject || ""}
          onChange={(e) => {
            setSelectedProject(e.target.value || null);
            const p = projects.find((p) => p.id === e.target.value);
            setActiveProject(p || null);
          }}
          className="rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300 outline-none"
        >
          <option value="">Select project...</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-6">
        {!selectedProject ? (
          <div className="flex h-full items-center justify-center text-sm text-zinc-500">
            Select or create a project to start chatting
          </div>
        ) : messages.length === 0 && !streaming ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 text-zinc-500">
            <div className="text-4xl">💬</div>
            <p className="text-sm">Start a conversation</p>
          </div>
        ) : (
          <div className="mx-auto max-w-3xl space-y-4">
            {messages.map((msg) => (
              <ChatMessage key={msg.id} message={msg} />
            ))}
            {streaming && streamingContent && (
              <ChatMessage
                message={{
                  id: "streaming",
                  role: "assistant",
                  content: streamingContent,
                  createdAt: new Date().toISOString(),
                  projectId: selectedProject,
                }}
                streaming
              />
            )}
            {error && (
              <div className="rounded-lg bg-red-500/10 px-4 py-2 text-sm text-red-400">
                {error}
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input */}
      <ChatInput onSend={handleSend} disabled={!selectedProject || streaming} />
    </div>
  );
}
